import { Capacitor } from "@capacitor/core";
import type { PermissionState, PluginListenerHandle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { PushTap, resolvePushTap } from "./pushTap";

/**
 * Native push, iOS only (Android is not live: no FCM, no Firebase). The ONLY
 * file that imports the push plugin.
 *
 * Every call degrades silently. A binary built before the plugin was added —
 * they live in the wild indefinitely — reports it unavailable, and alerts
 * still reach that user in-app and by email. Nothing here may ever throw into
 * a user flow, least of all sign-out.
 *
 * Deliberately does NOT import lib/platform: platform's session-gone redirect
 * calls unregisterPush, so the dependency runs one way only. That is why the
 * bearer token is passed in rather than read here.
 */

const API_URL = process.env.REACT_APP_API_URL ?? "http://localhost:8000";

const PUSH_TOKEN_KEY = "insight:push-token";
const SOFT_ASK_SHOWN_KEY = "insight:push-soft-ask-shown";

// APNs answers in well under a second. No answer at all means a binary whose
// AppDelegate does not forward the token callbacks.
const REGISTRATION_TIMEOUT_MS = 10_000;
// The longest sign-out cleanup may run before its caller is released.
export const UNREGISTER_TIMEOUT_MS = 3_000;

export type PushPermission = "granted" | "denied" | "prompt" | "unavailable";

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const isPushAvailable = (): boolean => {
  try {
    return Capacitor.getPlatform() === "ios" && Capacitor.isPluginAvailable("PushNotifications");
  } catch {
    return false;
  }
};

const toPushPermission = (state: PermissionState): PushPermission => {
  if (state === "granted" || state === "denied") return state;
  return "prompt";
};

/** Never prompts. `prompt` means iOS has not asked this install yet. */
export const getPushPermission = async (): Promise<PushPermission> => {
  if (!isPushAvailable()) return "unavailable";
  try {
    const { receive } = await PushNotifications.checkPermissions();
    return toPushPermission(receive);
  } catch {
    return "unavailable";
  }
};

/** Shows the system prompt. iOS shows it once per install, then just reports the answer. */
export const requestPushPermission = async (): Promise<PushPermission> => {
  if (!isPushAvailable()) return "unavailable";
  try {
    const { receive } = await PushNotifications.requestPermissions();
    return toPushPermission(receive);
  } catch {
    return "unavailable";
  }
};

// --- The last token this install registered --------------------------------
// Kept so sign-out can delete exactly that row. localStorage survives
// relaunches in the WebView, like the other per-install flags.

const storePushToken = (token: string): void => {
  try {
    localStorage.setItem(PUSH_TOKEN_KEY, token);
  } catch {
    // Storage unavailable — sign-out still unregisters at the OS level
  }
};

const takeStoredPushToken = (): string | null => {
  try {
    const token = localStorage.getItem(PUSH_TOKEN_KEY);
    localStorage.removeItem(PUSH_TOKEN_KEY);
    return token;
  } catch {
    return null;
  }
};

/**
 * Register with APNs and resolve with the device token, or null when there is
 * none to be had (no plugin, permission not granted, APNs refused, no answer).
 * Does not prompt — call it only once permission is granted.
 */
export const registerForPush = async (): Promise<string | null> => {
  if (!isPushAvailable()) return null;
  const handles: PluginListenerHandle[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const token = await new Promise<string | null>((resolve, reject) => {
      timer = setTimeout(() => resolve(null), REGISTRATION_TIMEOUT_MS);
      Promise.all([
        PushNotifications.addListener("registration", ({ value }) => resolve(value)),
        PushNotifications.addListener("registrationError", () => resolve(null)),
      ])
        .then((added) => {
          handles.push(...added);
          return PushNotifications.register();
        })
        .catch(reject);
    });
    if (token) storePushToken(token);
    return token;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    handles.forEach((handle) => void handle.remove().catch(() => undefined));
  }
};

// An async function so a synchronous throw from the bridge becomes a rejection,
// while the native call itself is still dispatched before anything is awaited.
const unregisterFromOs = async (): Promise<void> => {
  if (!isPushAvailable()) return;
  await PushNotifications.unregister();
};

// Plain fetch, not the axios instance: a 401 here (the session is often
// already gone) must not set off the interceptor's session check and redirect.
const deleteDeviceToken = async (
  deviceToken: string | null,
  authToken: string | null,
): Promise<void> => {
  if (!deviceToken || !authToken) return;
  await fetch(`${API_URL}/device-tokens/${encodeURIComponent(deviceToken)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${authToken}` },
    // The session-gone path reloads the WebView straight afterwards
    keepalive: true,
  });
};

/**
 * Sign-out cleanup, for EVERY way of becoming signed out. Two halves, started
 * together:
 *
 * - the OS-level unregister, which needs no API: it invalidates the APNs
 *   token, so a push queued for the old account is never delivered;
 * - a best-effort `DELETE /device-tokens/{token}`, which only works while
 *   `authToken` is still a live session — pass the bearer token read BEFORE it
 *   is cleared or revoked.
 *
 * Never throws and never takes longer than UNREGISTER_TIMEOUT_MS; callers fire
 * and forget it so sign-out is not delayed at all. Signing in registers again,
 * and the server repoints the token row to the new account.
 */
export const unregisterPush = async (authToken: string | null): Promise<void> => {
  try {
    if (Capacitor.getPlatform() !== "ios") return;
    const deviceToken = takeStoredPushToken();
    await Promise.race([
      Promise.allSettled([unregisterFromOs(), deleteDeviceToken(deviceToken, authToken)]),
      wait(UNREGISTER_TIMEOUT_MS),
    ]);
  } catch {
    // Cleanup is best-effort
  }
};

/**
 * Whether to show the push soft-ask now. True at most ONCE per install, and
 * only while iOS has not been asked: an install that already answered — yes
 * or no — is never nagged. Claims the one showing as it returns true.
 */
export const claimPushSoftAsk = async (): Promise<boolean> => {
  if (!isPushAvailable()) return false;
  try {
    if (localStorage.getItem(SOFT_ASK_SHOWN_KEY) === "true") return false;
    if ((await getPushPermission()) !== "prompt") return false;
    localStorage.setItem(SOFT_ASK_SHOWN_KEY, "true");
    return true;
  } catch {
    // No storage, no way to keep the "once" promise — so never ask
    return false;
  }
};

// --- Listeners --------------------------------------------------------------

const listen = (add: () => Promise<PluginListenerHandle>): (() => void) => {
  if (!isPushAvailable()) return () => undefined;
  let handle: Promise<PluginListenerHandle | null>;
  try {
    handle = add().catch(() => null);
  } catch {
    return () => undefined;
  }
  return () => {
    void handle.then((added) => added?.remove()).catch(() => undefined);
  };
};

/**
 * A push was TAPPED (not dismissed). Capacitor holds a cold-start tap until the
 * first listener is added, so this also delivers the tap that launched the app.
 * Returns an unsubscribe.
 */
export const addPushTapListener = (callback: (tap: PushTap) => void): (() => void) =>
  listen(() =>
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      if (action.actionId !== "tap") return;
      callback(resolvePushTap(action.notification?.data));
    }),
  );

/** A push arrived while the app was in the foreground (iOS shows no banner then). */
export const addPushReceivedListener = (callback: () => void): (() => void) =>
  listen(() => PushNotifications.addListener("pushNotificationReceived", () => callback()));
